import React, { useState } from "react";
import { BUSINESS } from "@/data/business";
import { BookButton } from "@/components/site/BookButton";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ZoomIn } from "lucide-react";

const GalleryHeader = () => (
  <section className="relative overflow-hidden">
    <div className="hero-accent absolute inset-0" aria-hidden="true" />
    <div className="container-x relative py-14 text-center sm:py-20">
      <Reveal className="mx-auto max-w-2xl">
        <span className="eyebrow">Gallery</span>
        <h1 className="mt-4 text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-6xl">
          Real clients, real results
        </h1>
        <p className="mt-6 text-base leading-relaxed text-[color:var(--text-muted)] sm:text-lg">
          A look at before-and-after transformations from treatments at Bahra Skincare.
        </p>
      </Reveal>
    </div>
  </section>
);

const GalleryGrid = ({ items, onSelect }) => (
  <div data-testid="before-after-gallery-grid" className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
    {items.map((g, i) => (
      <Reveal key={g.src} delay={i * 0.06}>
        <button
          type="button"
          data-testid={`before-after-gallery-item-${i}`}
          onClick={() => onSelect(g)}
          className="group relative block w-full overflow-hidden rounded-[18px] border border-[color:rgba(43,31,23,0.1)] bg-[color:var(--surface)] text-left glass-hover"
        >
          <img
            src={g.src}
            alt={g.alt}
            className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 flex items-end bg-gradient-to-t from-[rgba(43,31,23,0.55)] via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <div className="flex w-full items-center justify-between p-5">
              <span className="text-sm font-semibold text-[#F6F1E8]">{g.title}</span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FFFCF6]/85 text-[color:var(--text)]">
                <ZoomIn size={16} strokeWidth={1.75} />
              </span>
            </div>
          </div>
          <div className="p-4 sm:hidden">
            <span className="text-sm font-semibold text-[color:var(--text)]">{g.title}</span>
          </div>
        </button>
      </Reveal>
    ))}
  </div>
);

const GalleryLightbox = ({ active, onClose }) => (
  <Dialog open={!!active} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      data-testid="gallery-lightbox-dialog"
      aria-describedby={undefined}
      className="max-w-3xl border-[color:rgba(43,31,23,0.12)] bg-[color:var(--bg)] p-3 sm:p-4"
    >
      {active && (
        <>
          <DialogTitle className="sr-only">{active.title}</DialogTitle>
          <img src={active.src} alt={active.alt} className="max-h-[78vh] w-full rounded-[12px] object-contain" />
          <p className="px-1 pb-1 pt-2 text-center text-sm font-medium text-[color:var(--text)]">{active.title}</p>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const GalleryCta = () => (
  <section className="section-pad pt-0">
    <div className="container-x">
      <Reveal>
        <div className="glass-strong px-6 py-12 text-center sm:px-12 sm:py-16">
          <h2 className="mx-auto max-w-2xl text-3xl font-light tracking-tight sm:text-4xl">
            Your transformation starts here
          </h2>
          <div className="mt-8 flex justify-center">
            <BookButton testId="gallery-book-appointment-button" className="px-8 py-3.5" />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Before & After Gallery | Real Client Results | Bahra Skincare"
        description="See real before-and-after transformations from facials, Derma Pen treatments and chemical exfoliation at Bahra Skincare in Niles, IL. Photos shared with client permission."
        path="/gallery"
      />
      <GalleryHeader />

      {/* Grid */}
      <section className="section-pad pt-4">
        <div className="container-x">
          <GalleryGrid items={BUSINESS.gallery} onSelect={setActive} />
          <p className="mt-8 text-center text-xs text-[color:var(--text-subtle)]">
            Individual results may vary. Photos shared with client permission.
          </p>
        </div>
      </section>

      <GalleryLightbox active={active} onClose={() => setActive(null)} />
      <GalleryCta />
    </div>
  );
}
